/**
 * The shell's camera pick, for what renders above the router: the rule alert toasts sit at the
 * app root, where no PanelContext reaches. The snapshot says whether a shell is mounted to take
 * a pick; the standalone pages mount none.
 */

import type { ExternalStore, Unsubscribe } from '@/types/common';
import type { CameraPickAction, SelectCamera } from '@/types/panels';

interface ShellCameraPick extends ExternalStore<boolean> {
    /** Takes the picks while the shell is mounted; returns the unmount. */
    mount(select: SelectCamera): Unsubscribe;
    /** Hands a pick to the mounted shell; lands nowhere while none is mounted. */
    select: SelectCamera;
}

export function createShellCameraPick(): ShellCameraPick {
    let selectCamera: SelectCamera | null = null;
    const listeners = new Set<() => void>();
    const notify = (): void => {
        for (const listener of [...listeners]) {
            listener();
        }
    };
    return {
        mount(select: SelectCamera): Unsubscribe {
            selectCamera = select;
            notify();
            return (): void => {
                // A later mount took over; its own unmount clears it.
                if (selectCamera !== select) {
                    return;
                }
                selectCamera = null;
                notify();
            };
        },
        select: (cameraId, opts): CameraPickAction | undefined => selectCamera?.(cameraId, opts),
        subscribe(listener: () => void): Unsubscribe {
            listeners.add(listener);
            return (): void => {
                listeners.delete(listener);
            };
        },
        getSnapshot: (): boolean => selectCamera !== null,
    };
}

export const shellCameraPick = createShellCameraPick();
